import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, useIsAdmin } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import Header from "@/components/Header";
import MatchScoreRing from "@/components/MatchScoreRing";
import { Sparkles, CheckCircle2, XCircle, EyeOff, ArrowRight, Trophy, MapPin, GraduationCap, ShieldCheck, ShieldAlert, Lock, Award, Copy, Wand2 } from "lucide-react";
import CoachingTreePanel from "@/components/CoachingTreePanel";
import { ProfileCompletion } from "@/components/ProfileCompletion";

export default function CoachDashboard() {
  const { user } = useAuth();
  const isAdmin = useIsAdmin();
  const [profile, setProfile] = useState(null);
  const [opps, setOpps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [bioUrl, setBioUrl] = useState("");
  const [prefilling, setPrefilling] = useState(false);
  const [overwriteMode, setOverwriteMode] = useState(false);

  const load = async () => {
    try {
      const [p, o] = await Promise.all([
        api.get("/coach/profile"),
        api.get("/coach/opportunities").catch(() => ({ data: [] })),
      ]);
      setProfile(p.data);
      setOpps(o.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runEnrichment = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/coach/profile/enrich");
      setProfile(data);
      toast.success("Enrichment scan complete. Review suggestions below.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enrichment failed");
    } finally {
      setBusy(false);
    }
  };

  const decide = async (sid, decision) => {
    try {
      await api.post("/coach/profile/enrichment/decide", { suggestion_id: sid, decision });
      const { data } = await api.get("/coach/profile");
      setProfile(data);
      toast.success(`Marked as ${decision}.`);
    } catch (e) {
      toast.error("Failed");
    }
  };

  if (loading) return <Shell><div className="p-10 heading text-3xl">LOADING…</div></Shell>;

  const qbr = profile?.qbr?.score || 0;
  const bestLevel = profile?.qbr?.best_target_level || null;
  const cirByTarget = profile?.cir_by_target || {};
  const pending = (profile?.enrichment_suggestions || []).filter((s) => s.status === "pending");
  const isVerified = (profile?.verification_status || "unverified") === "verified";

  const verifyProfile = async () => {
    try {
      const approved = (profile.enrichment_suggestions || []).filter((s) => s.status === "approved").map((s) => s.id);
      const { data } = await api.post("/coach/verify", { verified_fields: ["philosophy", "playing_history", "education", "coaching_history"], enrichments_approved: approved });
      setProfile(data);
      toast.success("Profile verified. You're 3× more discoverable now.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verification failed");
    }
  };

  const prefillFromBio = async () => {
    if (!bioUrl.startsWith("http")) { toast.error("Paste a full http(s) URL"); return; }
    if (overwriteMode && !window.confirm("Overwrite mode: this will REPLACE any fields you've already filled in with whatever the AI extracts. Continue?")) return;
    setPrefilling(true);
    try {
      const { data } = await api.post("/coaches/self-prefill-from-url", { url: bioUrl.trim(), overwrite: overwriteMode });
      setProfile(data.coach);
      const filled = data?.fields_filled?.length || 0;
      if (filled) toast.success(`${data.overwrite ? "Overwrote" : "Pre-filled"} ${filled} field${filled === 1 ? "" : "s"} — review below and hit VERIFY when ready.`, { duration: 6000 });
      else toast.info("Nothing new to fill — your profile already has most of this.");
      setBioUrl("");
      setOverwriteMode(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Prefill failed");
    } finally {
      setPrefilling(false);
    }
  };

  const isThinProfile = (profile?.coaching_history || []).length === 0 && !profile?.philosophy;
  const hasBioPrefillHistory = !!profile?.bio_source_url;

  const isCollegeLevel = ["College", "College Club"].includes(profile?.primary_level);
  const isScholasticLevel = ["HS", "MS"].includes(profile?.primary_level);
  const showCrossLevelPrompt =
    profile &&
    !profile.cross_level_prompt_answered &&
    ((isCollegeLevel && !profile.open_to_hs_jobs) || (isScholasticLevel && !profile.open_to_college_jobs));

  const answerCrossLevel = async (willing) => {
    const payload = isCollegeLevel ? { open_to_hs_jobs: willing } : { open_to_college_jobs: willing };
    try {
      await api.post("/coach/profile/cross-level", payload);
      setProfile({ ...profile, cross_level_prompt_answered: true, ...payload });
      toast.success(willing ? "Noted — you'll now surface in those searches too." : "Got it — we won't show you in those searches.");
    } catch (e) {
      toast.error("Failed to save — try again");
    }
  };

  const showCommitmentPrompt = profile && !profile.commitment_prompt_answered && !showCrossLevelPrompt;

  const answerCommitment = async (pref) => {
    try {
      await api.post("/coach/profile/commitment", { position_type_pref: pref });
      setProfile({ ...profile, commitment_prompt_answered: true, position_type_pref: pref });
      toast.success(pref === "full_time" ? "Got it — full-time roles only." : "Noted — you'll surface in part-time / stipend searches.");
    } catch (e) {
      toast.error("Failed to save — try again");
    }
  };

  return (
    <Shell>
      <div className="px-4 md:px-8 lg:px-12 py-8" data-testid="coach-dashboard">
        <ProfileCompletion />
        {showCommitmentPrompt && (
          <div className="border border-volt p-4 bg-[#0E1206] flex items-start gap-3 mb-6" data-testid="commitment-prompt">
            <div className="flex-1">
              <div className="heading text-lg" data-testid="commitment-prompt-question">OPEN TO PART-TIME / STIPEND ROLES?</div>
              <p className="text-neutral-300 text-sm mt-1">
                Many great programs — especially HS varsity — hire part-time coaches on a seasonal stipend. It&apos;s a perfect fit alongside a day job, and ADs specifically search for coaches open to it. You can change this anytime in your profile.
              </p>
              <div className="mt-3 flex flex-wrap gap-3">
                <button data-testid="commitment-either-btn" onClick={() => answerCommitment("either")} className="border border-volt text-volt px-4 py-1.5 uppercase tracking-widest text-xs hover:bg-volt hover:text-black transition-colors">Yes — full-time or part-time</button>
                <button data-testid="commitment-parttime-btn" onClick={() => answerCommitment("part_time")} className="border border-volt text-volt px-4 py-1.5 uppercase tracking-widest text-xs hover:bg-volt hover:text-black transition-colors">Part-time (stipend) only</button>
                <button data-testid="commitment-fulltime-btn" onClick={() => answerCommitment("full_time")} className="border border-neutral-700 text-neutral-400 px-4 py-1.5 uppercase tracking-widest text-xs hover:border-neutral-500 transition-colors">Full-time only</button>
              </div>
            </div>
          </div>
        )}
        {showCrossLevelPrompt && (
          <div className="border border-volt p-4 bg-[#0E1206] flex items-start gap-3 mb-6" data-testid="cross-level-prompt">
            <div className="flex-1">
              <div className="heading text-lg" data-testid="cross-level-prompt-question">
                {isCollegeLevel ? "WOULD YOU CONSIDER THE RIGHT HS PROGRAM?" : "WOULD YOU CONSIDER THE RIGHT COLLEGE PROGRAM?"}
              </div>
              <p className="text-neutral-300 text-sm mt-1">
                {isCollegeLevel
                  ? "Some of the best HS head-coach jobs in the country are searching right now. Opting in makes you visible in HS-level stealth searches — you can change this anytime in your profile."
                  : "College programs run confidential searches on this platform. Opting in makes you visible when they search at your level — you can change this anytime in your profile."}
              </p>
              <div className="mt-3 flex gap-3">
                <button
                  data-testid="cross-level-yes-btn"
                  onClick={() => answerCrossLevel(true)}
                  className="border border-volt text-volt px-4 py-1.5 uppercase tracking-widest text-xs hover:bg-volt hover:text-black transition-colors"
                >
                  Yes, I'd consider it
                </button>
                <button
                  data-testid="cross-level-no-btn"
                  onClick={() => answerCrossLevel(false)}
                  className="border border-neutral-700 text-neutral-400 px-4 py-1.5 uppercase tracking-widest text-xs hover:border-neutral-500 transition-colors"
                >
                  No thanks
                </button>
              </div>
            </div>
          </div>
        )}
        {!isVerified && (
          <div className="border border-blaze p-4 bg-[#1A0A0A] flex items-start gap-3 mb-6" data-testid="dashboard-verify-banner">
            <ShieldAlert className="text-blaze shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <div className="heading text-lg">CONFIRM YOUR PROFILE IS ACCURATE</div>
              <p className="text-neutral-300 text-sm mt-1">Review your data + any AI-enriched additions. Verified profiles get a blue check, surface higher in ranked searches, and are 3× more likely to be reviewed by ADs.</p>
            </div>
            <button onClick={verifyProfile} data-testid="dashboard-verify-btn" className="bg-blaze text-white heading text-sm px-4 py-2 hover:glow-blaze whitespace-nowrap">VERIFY NOW</button>
          </div>
        )}
        {isVerified && (
          <div className="border border-volt/40 bg-[#0A101A] px-4 py-2 flex items-center gap-2 mb-6" data-testid="dashboard-verified-badge">
            <ShieldCheck className="text-volt" size={18} />
            <span className="text-sm text-volt heading">VERIFIED COACH</span>
            <span className="text-xs text-neutral-500 ml-2">{new Date(profile.verified_at).toLocaleDateString()}</span>
          </div>
        )}

        {(isThinProfile || hasBioPrefillHistory) && (
          <div className="border border-volt/40 bg-[#0A101A] p-5 mb-6" data-testid="dashboard-bio-prefill-panel">
            <div className="flex items-center gap-2 text-xs tracking-widest text-volt heading">
              <Wand2 size={14} /> {isThinProfile ? "QUICK-START YOUR PROFILE" : "RE-SYNC FROM BIO URL"}
            </div>
            <p className="text-neutral-300 text-sm mt-2">
              {isThinProfile
                ? "Your profile is empty. Paste your athletic-dept bio, MaxPreps profile, LinkedIn, or news article URL and we'll auto-fill your coaching history, playing history, honors, and headline."
                : "Paste a bio URL to fill in more fields — or turn on Overwrite Mode to replace what's there with a fresh AI extract."}
            </p>
            <div className="mt-3 flex flex-col md:flex-row gap-2">
              <input
                data-testid="dashboard-bio-url-input"
                type="url"
                value={bioUrl}
                onChange={(e) => setBioUrl(e.target.value)}
                placeholder="https://university.edu/athletics/coaches/your-bio"
                className="flex-1 bg-black border border-neutral-800 px-3 py-2 text-sm focus:border-volt outline-none"
              />
              <button
                onClick={prefillFromBio}
                disabled={prefilling || !bioUrl}
                data-testid="dashboard-bio-prefill-btn"
                className="bg-volt text-white heading text-xs tracking-widest px-5 py-2.5 hover:glow-volt disabled:opacity-40 flex items-center gap-2 whitespace-nowrap"
              >
                <Wand2 size={14} /> {prefilling ? "READING…" : "AUTO-FILL"}
              </button>
            </div>
            <label className="mt-3 inline-flex items-center gap-2 text-xs text-neutral-400 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="dashboard-bio-overwrite-toggle"
                checked={overwriteMode}
                onChange={(e) => setOverwriteMode(e.target.checked)}
                className="accent-blaze"
              />
              <span>
                <span className="text-blaze">OVERWRITE MODE</span> — replace any fields I&apos;ve already entered with the AI extract
              </span>
            </label>
            {profile?.bio_source_url && (
              <div className="mt-3 text-[11px] text-neutral-500">
                Last synced from <a href={profile.bio_source_url} target="_blank" rel="noreferrer" className="text-neutral-400 hover:text-volt underline">{profile.bio_source_url.slice(0, 60)}{profile.bio_source_url.length > 60 ? "…" : ""}</a>
                {profile.last_self_prefill_at && <> · {new Date(profile.last_self_prefill_at).toLocaleDateString()}</>}
              </div>
            )}
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            <div className="text-xs tracking-widest text-blaze">COACH HQ</div>
            <h1 className="heading text-4xl md:text-5xl lg:text-6xl mt-2">HELLO, {user.name.toUpperCase()}.</h1>
            <p className="text-neutral-400 mt-2 max-w-xl">{profile.headline || "Add a headline and philosophy to unlock matches."}</p>
          </div>

          <div className="lg:col-span-4 border border-neutral-800 bg-appcard p-6" data-testid="coach-qbr-card">
            <div className="flex items-center gap-6">
              <MatchScoreRing score={qbr} label="CIR" size={130} stroke={12} />
              <div className="min-w-0">
                <div className="heading text-2xl">COACH CIR</div>
                {profile?.qbr?.tier && (
                  <div className="inline-block mt-1 px-2 py-0.5 bg-blaze text-white text-[10px] tracking-widest" data-testid="qbr-tier-badge">
                    {profile.qbr.tier.toUpperCase()}
                  </div>
                )}
                {bestLevel && (
                  <div className="mt-2 text-[10px] tracking-widest text-volt font-bold" data-testid="qbr-best-fit">
                    BEST FIT · {bestLevel}
                  </div>
                )}
                <div className="text-neutral-300 text-xs mt-2">Your peak score across HS · D3 · D2 · D1.</div>
                <div className="text-mono text-neutral-400 text-xs mt-1">Record: {profile?.qbr?.record || "0-0"}</div>
              </div>
            </div>
            {/* Nudge to keep completing profile */}
            {qbr < 80 && (
              <Link
                to="/coach/profile"
                data-testid="cir-boost-nudge"
                className="mt-4 flex items-center justify-between gap-3 border-l-4 border-volt bg-volt/10 px-3 py-2 hover:bg-volt/20 transition-colors"
              >
                <div className="min-w-0">
                  <div className="text-[10px] tracking-widest text-volt font-bold">GROW YOUR CIR</div>
                  <div className="text-xs text-neutral-100 mt-0.5">
                    {qbr < 40 ? "Fill in playing history, honors, and mentors to unlock more points." :
                     qbr < 60 ? "Add championships, endorsements, and coaching outcomes to break 60." :
                     "You're close to Elite — add mentor pedigree + peer endorsements for the last lift."}
                  </div>
                </div>
                <ArrowRight className="text-volt shrink-0" size={16} />
              </Link>
            )}
          </div>
        </div>

        {/* Target-level breakdown — show how the coach ranks against each hire level */}
        {Object.keys(cirByTarget).length > 0 && (
          <div className="mt-6 border border-neutral-800 bg-appcard p-6" data-testid="cir-by-target-panel">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] tracking-widest text-volt font-bold">YOUR CIR BY HIRE LEVEL</div>
                <div className="heading text-2xl mt-1">HOW YOU STACK · HS · D3 · D2 · D1</div>
              </div>
              <div className="text-mono text-[10px] text-neutral-400 tracking-widest">MULTI-LEVEL SCORING</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              {["HS", "D3", "D2", "D1"].map((lvl) => {
                const res = cirByTarget[lvl] || {};
                const score = res.score || 0;
                const tier = res.tier || "";
                const isBest = lvl === bestLevel;
                return (
                  <div
                    key={lvl}
                    data-testid={`cir-target-${lvl}`}
                    className={`bg-appbg p-4 border-l-4 ${isBest ? "border-blaze" : "border-volt/40"}`}
                  >
                    <div className="flex items-baseline justify-between">
                      <div className={`heading text-2xl ${isBest ? "text-blaze" : "text-white"}`}>{lvl}</div>
                      {isBest && <div className="text-[9px] tracking-widest text-blaze font-bold">BEST FIT</div>}
                    </div>
                    <div className="heading text-4xl text-mono mt-2">
                      {score}<span className="text-neutral-500 text-base">/100</span>
                    </div>
                    {tier && (
                      <div className="text-[10px] tracking-widest text-neutral-300 mt-1">{tier.toUpperCase()}</div>
                    )}
                    {/* Progress bar */}
                    <div className="h-1.5 bg-neutral-800 mt-3">
                      <div
                        className={`h-1.5 ${isBest ? "bg-blaze" : "bg-volt"}`}
                        style={{ width: `${Math.min(100, Math.max(0, score))}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 text-xs text-neutral-300 leading-relaxed">
              Each level applies its own hiring bar — a D1 program weighs playing pedigree and elite recognition heavier;
              an HS program rewards tenure, community, and championship record. <b className="text-white">Complete your profile</b> to lift every column.
            </div>
          </div>
        )}

        {/* Coaching Tree — who they learned under + who they developed */}
        <div className="mt-6">
          <CoachingTreePanel
            mentorsInferred={profile?.mentors_inferred || []}
            disciples={profile?.disciples || []}
            variant="self"
            coachName={profile?.name || "You"}
          />
        </div>

        {/* CIR explainability bullets */}
        {(profile?.qbr?.bullets || []).length > 0 && (
          <div className="mt-4 border-l-2 border-volt pl-4 py-2" data-testid="qbr-bullets">
            <div className="text-volt text-[10px] tracking-widest mb-2">WHAT DRIVES YOUR SCORE</div>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm text-neutral-300">
              {profile.qbr.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-blaze mt-1">▸</span><span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* CIR breakdown */}
        <div className="mt-8 border border-neutral-800 p-6 bg-appcard" data-testid="qbr-breakdown">
          <div className="flex items-center justify-between mb-4">
            <div className="heading text-2xl">CIR BREAKDOWN</div>
            <div className="text-mono text-xs text-neutral-500">DETERMINISTIC · NON-LLM</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-neutral-800">
            {(profile?.qbr?.components || []).map((c) => (
              <div key={c.label} className="bg-appbg p-4">
                <div className="text-[10px] tracking-widest text-neutral-500">{c.label.toUpperCase()}</div>
                <div className="heading text-3xl mt-2 text-mono">
                  {c.value}<span className="text-neutral-600 text-lg">/{c.max}</span>
                </div>
                <div className="h-1 bg-neutral-800 mt-3">
                  <div className="h-1 bg-blaze" style={{ width: `${(c.value / c.max) * 100}%` }} />
                </div>
                {c.extra && <div className="text-mono text-xs text-neutral-400 mt-2">{c.extra}</div>}
              </div>
            ))}
          </div>
        </div>

        {/* Enrichment */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 border border-neutral-800 p-6 bg-appcard" data-testid="enrichment-panel">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs tracking-widest text-volt">COACHPROFILEAGENT</div>
                <div className="heading text-2xl mt-1">AI-ENRICHED FACTS</div>
              </div>
              <button
                onClick={runEnrichment}
                disabled={busy}
                data-testid="run-enrichment-btn"
                className="bg-volt text-white heading text-base px-5 py-3 hover:glow-volt transition-all disabled:opacity-50 flex items-center gap-2"
              >
                <Sparkles size={16} /> {busy ? "SCANNING…" : "RUN SCAN"}
              </button>
            </div>
            {pending.length === 0 ? (
              <p className="text-neutral-500 text-sm">No pending suggestions. Run a scan to scout public lacrosse sources.</p>
            ) : (
              <div className="space-y-3">
                {pending.map((s) => (
                  <div key={s.id} className="border-l-2 border-volt bg-neutral-900/40 p-4" data-testid={`enrichment-item-${s.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="heading text-lg">{s.title}</div>
                        <div className="text-neutral-400 text-sm mt-1">{s.description}</div>
                        <div className="text-mono text-xs text-neutral-500 mt-2">
                          {s.category.toUpperCase()} · {s.source} · {s.source_date?.slice(0, 10)} · CONF {Math.round(s.confidence * 100)}%
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => decide(s.id, "approve")} data-testid={`approve-${s.id}`} className="flex items-center gap-1 px-3 py-1.5 border border-green-700 text-green-400 text-xs uppercase tracking-widest hover:bg-green-900/20">
                        <CheckCircle2 size={14} /> Approve
                      </button>
                      <button onClick={() => decide(s.id, "dispute")} data-testid={`dispute-${s.id}`} className="flex items-center gap-1 px-3 py-1.5 border border-blaze text-blaze text-xs uppercase tracking-widest hover:bg-red-900/20">
                        <XCircle size={14} /> Dispute
                      </button>
                      <button onClick={() => decide(s.id, "hide")} data-testid={`hide-${s.id}`} className="flex items-center gap-1 px-3 py-1.5 border border-neutral-700 text-neutral-400 text-xs uppercase tracking-widest hover:bg-neutral-800">
                        <EyeOff size={14} /> Hide
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick stats */}
          <div className="space-y-6">
            <Stat icon={Trophy} label="Coaching stints" value={profile?.coaching_history?.length || 0} />
            <Stat icon={MapPin} label="Target regions" value={(profile?.geographic_regions || []).length || "—"} />
            <Stat icon={GraduationCap} label="Affiliations" value={(profile?.affiliations || []).join(" · ") || "Add to profile"} small />
            <Link to="/coach/profile" data-testid="edit-profile-link" className="block bg-blaze text-white heading text-lg px-6 py-3 hover:glow-blaze transition-all">
              EDIT PROFILE →
            </Link>
            <RequestEndorsementCard />
          </div>
        </div>

        {/* Top opportunities */}
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs tracking-widest text-blaze">MATCHENGINEAGENT</div>
              <div className="heading text-3xl mt-1">TOP OPPORTUNITIES</div>
            </div>
            <Link to="/coach/opportunities" data-testid="view-all-opps-link" className="text-volt hover:underline text-sm uppercase tracking-widest flex items-center gap-2">
              View all <ArrowRight size={14} />
            </Link>
          </div>
          {opps.length === 0 ? (
            <div className="border border-dashed border-neutral-800 p-10 text-center text-neutral-500">No live openings match your profile yet. Programs are posting daily — check back soon.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {opps.slice(0, 4).map(({ job, match, paywalled }) => (
                <Link
                  key={match.id}
                  to={`/jobs/${job.id}`}
                  data-testid={`opp-card-${job.id}`}
                  className={`border bg-appcard p-5 transition-colors block ${paywalled ? "border-volt/40 hover:border-volt" : "border-neutral-800 hover:border-blaze"}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-neutral-500 tracking-widest">{job.gender?.toUpperCase()} · {job.sport?.toUpperCase()} · {job.division || job.level}</div>
                      <div className="heading text-xl mt-1">{job.title}</div>
                      {paywalled ? (
                        <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-volt heading tracking-widest" data-testid={`opp-paywall-${job.id}`}>
                          <Lock size={12} /> PROGRAM DETAILS LOADING · FREE FOR COACHES
                        </div>
                      ) : (
                        <div className="text-neutral-400 text-sm truncate">{job.program_name} · {job.location}</div>
                      )}
                    </div>
                    <MatchScoreRing score={match.score} size={70} stroke={6} />
                  </div>
                  <div className={`mt-3 text-sm line-clamp-2 ${paywalled ? "text-neutral-500 italic" : "text-neutral-300"}`}>{match.rationale?.summary}</div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}

function Stat({ icon: Icon, label, value, small }) {
  return (
    <div className="border border-neutral-800 p-4 bg-appcard">
      <Icon className="text-volt" size={20} strokeWidth={1.5} />
      <div className="text-[10px] tracking-widest text-neutral-500 mt-2">{label.toUpperCase()}</div>
      <div className={`heading mt-1 ${small ? "text-base" : "text-3xl"}`}>{value}</div>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-appbg text-white">
      <Header />
      {children}
    </div>
  );
}

function RequestEndorsementCard() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ endorser_name: "", endorser_email: "", relationship: "" });
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.endorser_name || !form.relationship) return toast.warning("Endorser name and relationship are required.");
    setBusy(true);
    try {
      const { data } = await api.post("/coach/endorsements/request", form);
      const url = `${window.location.origin}${data.share_path}`;
      setLink(url);
      toast.success("Endorsement link created — share it with the endorser.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create endorsement link.");
    } finally { setBusy(false); }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(link);
    toast.success("Link copied. Paste it into a text, email, or DM.");
  };

  return (
    <div className="border border-blaze/40 p-5 bg-[#140909]" data-testid="request-endorsement-card">
      <div className="flex items-center gap-2 text-blaze">
        <Award size={18} />
        <div className="heading text-base tracking-widest">REQUEST AN ENDORSEMENT</div>
      </div>
      <p className="text-xs text-neutral-400 mt-2">HOF and national-champion coaches you played for can vouch for you. Their endorsement directly boosts your <b className="text-volt">CIR</b> through the Peer Endorsements component (up to 6 pts; HOF = 4 pts each).</p>
      {!open ? (
        <button onClick={() => setOpen(true)} data-testid="open-request-endorsement-btn" className="mt-3 w-full bg-blaze text-white heading text-sm py-2.5 hover:glow-blaze transition-all">
          REQUEST ONE NOW
        </button>
      ) : link ? (
        <div className="mt-3 space-y-2">
          <div className="text-[10px] tracking-widest text-volt">SHARE THIS LINK WITH THE ENDORSER</div>
          <div className="bg-appbg border border-neutral-800 p-2 text-xs break-all text-neutral-300" data-testid="endorsement-link">{link}</div>
          <button onClick={copy} data-testid="copy-endorsement-link-btn" className="w-full flex items-center justify-center gap-2 bg-volt text-white heading text-sm py-2.5 hover:glow-volt transition-all"><Copy size={14} /> COPY LINK</button>
          <button onClick={() => { setOpen(false); setLink(""); setForm({ endorser_name: "", endorser_email: "", relationship: "" }); }} className="w-full text-xs text-neutral-500 hover:text-white py-1">+ REQUEST ANOTHER</button>
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          <input className="w-full bg-appbg border border-neutral-800 px-3 py-2 text-xs text-white" data-testid="req-endorser-name" placeholder="Endorser's name (e.g. Charley Toomey)" value={form.endorser_name} onChange={(e) => setForm({ ...form, endorser_name: e.target.value })} />
          <input className="w-full bg-appbg border border-neutral-800 px-3 py-2 text-xs text-white" data-testid="req-endorser-email" placeholder="Email (optional)" value={form.endorser_email} onChange={(e) => setForm({ ...form, endorser_email: e.target.value })} />
          <input className="w-full bg-appbg border border-neutral-800 px-3 py-2 text-xs text-white" data-testid="req-endorser-relationship" placeholder="How they know you" value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} />
          <button onClick={submit} disabled={busy} data-testid="submit-endorsement-request-btn" className="w-full bg-blaze text-white heading text-sm py-2.5 hover:glow-blaze transition-all disabled:opacity-50">{busy ? "CREATING…" : "GENERATE LINK"}</button>
        </div>
      )}
    </div>
  );
}
